// @flow
import React, { useContext, useReducer } from 'react';
import { Formik, Form } from 'formik';
import classnames from 'classnames';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { WIDGET_EVENT_FORM_SUBMIT } from 'Utils/request/requestDictionary';
import { t } from 'Src/i18n';
import ReactAppContext from 'Context/ReactAppContext';
import { COLORS } from 'Components/utils/color/colors';
import { Confirmation } from 'Components/widgets/confirmation/Confirmation';
import { Button } from 'Components/frameworks/button/Button';
import { FormikInput as FormField } from 'Components/frameworks/formik/FormikInput';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { Title } from 'Components/frameworks/title/Title';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { MEETING_EVENT } from 'Tracking/constants';

import { getEventQuery, getEventSchema, getInitialFormValues } from './eventForm.utils';

type Event = {
  event:
    | { id: string }
    | {
        statusCode: number,
        statusMessage: string,
        errorCode: string,
        errorDescription: string,
      },
};

export type EventFormResponseTranslations = {
  title: string,
  text: string,
  link?: { label?: string, url?: string },
};

export type EventFormResort = {
  id: string,
  name: string,
  region: string,
};

type EventFormProps = {
  formError: EventFormResponseTranslations,
  formSuccess: EventFormResponseTranslations,
  regions: Array<string>,
  resorts: Array<EventFormResort>,
  eventTypeOptions: Array<{ value: string, name: string }>,
  userSalutationOptions: Array<{ value: string, name: string }>,
};

const FORM_ACTIONS = {
  SUBMIT_EVENT: 'SUBMIT_EVENT',
  SUBMIT_EVENT_ERROR: 'SUBMIT_EVENT_ERROR',
  SUBMIT_EVENT_SUCCESS: 'SUBMIT_EVENT_SUCCESS',
};
const FORM_STATUS = { NEW: 'neutral', SUCCESS: 'success', ERROR: 'error' };

export const EventForm = ({
  formError,
  formSuccess,
  regions,
  resorts,
  eventTypeOptions,
  userSalutationOptions,
}: EventFormProps) => {
  const appContext = useContext(ReactAppContext);
  const track = useTracking({
    categoryGA: MEETING_EVENT.REQUEST,
  });

  const [{ isSending, formStatus, confirmation }, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case FORM_ACTIONS.SUBMIT_EVENT:
          return { ...state, isSending: true };
        case FORM_ACTIONS.SUBMIT_EVENT_ERROR:
          return { isSending: false, formStatus: FORM_STATUS.ERROR, confirmation: formError };
        case FORM_ACTIONS.SUBMIT_EVENT_SUCCESS:
          return {
            isSending: false,
            formStatus: FORM_STATUS.SUCCESS,
            confirmation: formSuccess,
          };
        default:
          return state;
      }
    },
    {
      isSending: false,
      formStatus: FORM_STATUS.NEW,
      confirmation: {},
    },
  );

  return (
    <SiteContainer className="py-10" id="event-form">
      <LineTitle>{t('eventForm.formTitle')}</LineTitle>
      <Formik
        initialValues={getInitialFormValues()}
        validationSchema={getEventSchema(resorts.map(resort => resort.id))}
        onSubmit={async values => {
          dispatch({ type: FORM_ACTIONS.SUBMIT_EVENT });
          const query = getEventQuery(values);
          const labelGA = values.resort || null;
          try {
            const { event }: Event = await graphQLClient.runNoCacheQuery(query, appContext, {
              requestName: WIDGET_EVENT_FORM_SUBMIT,
            });
            if (event.errorCode) {
              dispatch({ type: FORM_ACTIONS.SUBMIT_EVENT_ERROR });
              track({
                actionGA: MEETING_EVENT.SEND_REQUEST_ERROR,
                labelGA,
              });
            } else {
              dispatch({ type: FORM_ACTIONS.SUBMIT_EVENT_SUCCESS });
              track({
                actionGA: MEETING_EVENT.SEND_REQUEST_SUCCESS,
                labelGA,
              });
            }
          } catch (error) {
            dispatch({ type: FORM_ACTIONS.SUBMIT_EVENT_ERROR });
            track({
              actionGA: MEETING_EVENT.SEND_REQUEST_ERROR,
              labelGA,
            });
          }
        }}
      >
        {({ values, isValid }) => (
          <Form data-testid="form">
            <Title className="mt-8 mb-3 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
              {t('eventForm.packageSection.title')}
            </Title>
            <div className="flex flex-wrap">
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  dataTestId="event-type"
                  isRequired
                  label={t('eventForm.packageSection.eventTypeLabel')}
                  name="type"
                  options={eventTypeOptions}
                  placeholder={t('eventForm.packageSection.eventTypePlaceholder')}
                  type="select"
                />
              </div>
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  dataTestId="event-participants"
                  isRequired
                  label={t('eventForm.packageSection.numberOfParticipantsLabel')}
                  name="numberOfParticipants"
                  optimized
                  placeholder={t('eventForm.packageSection.numberOfParticipantsPlaceholder')}
                  type="number"
                />
              </div>
              <p className="w-full mb-2 px-2 pt-2 text-base font-bold">
                {t('eventForm.packageSection.packageServicesSection.packageServicesLabel')}
              </p>
              <div className="flex flex-wrap w-full md:w-1/2">
                <div className="w-full md:w-1/2 p-2">
                  <FormField
                    className="mb-3"
                    label={t('eventForm.packageSection.packageServicesSection.roomsCheckboxLabel')}
                    name="withRooms"
                    optimized
                    type="checkbox"
                  />
                  <FormField
                    isDisabled={!values.withRooms}
                    label={t('eventForm.packageSection.packageServicesSection.roomsInputLabel')}
                    name="numberOfRooms"
                    placeholder={t(
                      'eventForm.packageSection.packageServicesSection.roomsInputPlaceholder',
                    )}
                    type="number"
                  />
                </div>
                <div className="w-full md:w-1/2 p-2">
                  <FormField
                    className="mb-3"
                    label={t(
                      'eventForm.packageSection.packageServicesSection.transportationCheckboxLabel',
                    )}
                    name="withTransport"
                    optimized
                    type="checkbox"
                  />
                  <FormField
                    isDisabled={!values.withTransport}
                    label={t(
                      'eventForm.packageSection.packageServicesSection.transportationInputLabel',
                    )}
                    name="departureCity"
                    placeholder={t(
                      'eventForm.packageSection.packageServicesSection.transportationInputPlaceholder',
                    )}
                    type="text"
                  />
                </div>
                <div className="w-full md:w-1/2 p-2">
                  <FormField
                    className="mb-3"
                    label={t(
                      'eventForm.packageSection.packageServicesSection.meetingRoomsCheckboxLabel',
                    )}
                    name="withMeetingRooms"
                    optimized
                    type="checkbox"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  className="mb-3"
                  label={t(
                    'eventForm.packageSection.packageServicesSection.otherServicesCheckboxLabel',
                  )}
                  name="withServices"
                  optimized
                  type="checkbox"
                />
                <FormField
                  isDisabled={!values.withServices}
                  label={t(
                    'eventForm.packageSection.packageServicesSection.otherServicesInputLabel',
                  )}
                  name="logisticsComment"
                  placeholder={t(
                    'eventForm.packageSection.packageServicesSection.otherServicesInputPlaceholder',
                  )}
                  type="textarea"
                />
              </div>
            </div>
            <Title className="mt-8 mb-3 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
              {t('eventForm.staySection.title')}
            </Title>
            <div className="flex flex-wrap">
              <div className="w-full md:w-1/2">
                <div className="w-full p-2">
                  <FormField
                    dataTestId="event-region"
                    label={t('eventForm.staySection.destinationLabel')}
                    name="region"
                    options={regions.map(region => ({ name: region, value: region })).sort()}
                    placeholder={t('eventForm.staySection.destinationPlaceholder')}
                    type="select"
                  />
                </div>
                <div className="w-full p-2">
                  <FormField
                    dataTestId="event-resort"
                    label={t('eventForm.staySection.resortLabel')}
                    name="resort"
                    options={resorts
                      .filter(resort => !values.region || resort.region === values.region)
                      .map(resort => ({ name: resort.name, value: resort.id }))}
                    placeholder={t('eventForm.staySection.resortPlaceholder')}
                    type="select"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/4 p-2">
                <FormField
                  isDateRange
                  label={t('eventForm.staySection.datesLabel')}
                  name="dates"
                  placeholder={t('eventForm.staySection.datesPlaceholder')}
                  type="datepicker"
                />
              </div>
              <div className="w-full md:w-1/4 p-2">
                <FormField
                  className="mb-3 md:mt-9"
                  label={t('eventForm.staySection.flexibleDatesLabel')}
                  name="withFlexibleDates"
                  type="checkbox"
                />
              </div>
            </div>
            <Title className="mt-8 mb-3 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
              {t('eventForm.userSection.title')}
            </Title>
            <div className="flex flex-wrap">
              <div className="w-full">
                <div className="w-full md:w-1/2 p-2">
                  <FormField
                    dataTestId="event-owner-company"
                    label={t('eventForm.userSection.companyLabel')}
                    name="company"
                    optimized
                    placeholder={t('eventForm.userSection.companyPlaceholder')}
                    type="text"
                  />
                </div>
              </div>
              <div className="w-1/2 md:w-1/6 p-2">
                <FormField
                  dataTestId="event-owner-salutation"
                  isRequired
                  label={t('eventForm.userSection.salutationLabel')}
                  name="salutation"
                  optimized
                  options={userSalutationOptions}
                  placeholder={t('eventForm.userSection.salutationPlaceholder')}
                  type="select"
                />
              </div>
              <div className="w-full md:w-1/3 p-2">
                <FormField
                  dataTestId="event-owner-firstName"
                  isRequired
                  label={t('eventForm.userSection.firstNameLabel')}
                  name="firstName"
                  optimized
                  placeholder={t('eventForm.userSection.firstNamePlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  dataTestId="event-owner-lastName"
                  isRequired
                  label={t('eventForm.userSection.lastNameLabel')}
                  name="lastName"
                  optimized
                  placeholder={t('eventForm.userSection.lastNamePlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  dataTestId="event-owner-email"
                  isRequired
                  label={t('eventForm.userSection.emailLabel')}
                  name="email"
                  optimized
                  placeholder={t('eventForm.userSection.emailPlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  dataTestId="event-owner-phone"
                  isRequired
                  label={t('eventForm.userSection.phoneLabel')}
                  name="phone"
                  optimized
                  placeholder={t('eventForm.userSection.phonePlaceholder')}
                  type="text"
                />
              </div>
              <p className="w-full mb-2 px-2 pt-2 text-base font-bold">
                {t('eventForm.userSection.addressLabel')}
              </p>
              <div className="w-full md:w-1/2 p-2">
                <FormField
                  dataTestId="event-owner-address"
                  isRequired
                  label={t('eventForm.userSection.streetLabel')}
                  name="address"
                  optimized
                  placeholder={t('eventForm.userSection.streetPlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-1/2 md:w-1/6 p-2">
                <FormField
                  dataTestId="event-owner-postcode"
                  isRequired
                  label={t('eventForm.userSection.postcodeLabel')}
                  name="postcode"
                  optimized
                  placeholder={t('eventForm.userSection.postcodePlaceholder')}
                  type="text"
                />
              </div>
              <div className="w-full md:w-1/3">
                <div className="w-full p-2">
                  <FormField
                    dataTestId="event-owner-city"
                    isRequired
                    label={t('eventForm.userSection.cityLabel')}
                    name="city"
                    optimized
                    placeholder={t('eventForm.userSection.cityPlaceholder')}
                    type="text"
                  />
                </div>
                <div className="w-full p-2">
                  <FormField
                    dataTestId="event-owner-country"
                    isRequired
                    label={t('eventForm.userSection.countryLabel')}
                    name="country"
                    optimized
                    placeholder={t('eventForm.userSection.countryPlaceholder')}
                    type="text"
                  />
                </div>
              </div>
            </div>
            <Title className="mt-8 mb-3 px-2" color={COLORS.BLUE} hLevel={3} theme="title-sans-4">
              {t('eventForm.commentSection.title')}
            </Title>
            <div className="flex flex-wrap">
              <div className="w-full p-2">
                <FormField
                  label={t('eventForm.commentSection.commentLabel')}
                  name="generalComment"
                  placeholder={t('eventForm.commentSection.commentPlaceholder')}
                  type="textarea"
                />
              </div>
              <div className="w-full p-2">
                <p>{t('eventForm.commentSection.optinLabel')}</p>
                <FormField
                  className="mb-3"
                  label={t('eventForm.commentSection.emailOptinLabel')}
                  name="optinEmail"
                  optimized
                  type="checkbox"
                />
                <FormField
                  className="mb-3"
                  label={t('eventForm.commentSection.smsOptinLabel')}
                  name="optinSMS"
                  optimized
                  type="checkbox"
                />
                <FormField
                  className="mb-3"
                  label={t('eventForm.commentSection.phoneOptinLabel')}
                  name="optinPhone"
                  optimized
                  type="checkbox"
                />
              </div>
            </div>
            <div
              className={classnames('mt-4', {
                'text-right': formStatus === FORM_STATUS.NEW,
                'flex justify-center': formStatus !== FORM_STATUS.NEW,
              })}
            >
              {formStatus === FORM_STATUS.NEW ? (
                <Button
                  colorBg={COLORS.BLUE}
                  colorBorder={COLORS.BLUE}
                  dataTestId="event-submit"
                  isDisabled={!isValid}
                  isLoading={isSending}
                  type="submit"
                >
                  {t('eventForm.formButton')}
                </Button>
              ) : (
                <Confirmation {...confirmation} status={formStatus} />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </SiteContainer>
  );
};
